import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { NumericField } from "@/components/NumericField";
import { SelectField } from "@/components/SelectField";
import {
  setBondBidResult,
  uploadBondFinalContract,
  getBondSubmission,
  FINAL_CONTRACT_ACCEPT,
  FINAL_CONTRACT_MAX_BYTES,
  type BondDocument,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { signedContractOf } from "@/lib/useBondDocuments";
import {
  bidExplanationRequired,
  bidGapPercent,
  ourBidFromOrder,
  THRESHOLD_PERCENT,
} from "@/lib/bidResult";
import { formatBondCurrency, formatBondDate } from "@/lib/bondValueDisplay";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { BOND_FINAL_STEP_KEY } from "@/lib/brokerBonds";

// BRK-S141 — the broker's report on their own bid.
//
// Offered on a SUBMITTED order only: a draft has no filed bid to report against,
// and the write answers 409 `NOT_SUBMITTED` for one. The panel is the single
// thing a filed order can still change from the profile.
//
// ── THE 10% RULE IS EXPLAINED HERE AND ENFORCED UPSTREAM ─────────────────────
//
// Discovery holds the submitted bid and decides whether an explanation was
// required; PMS deliberately does not pre-check it and passes the 422 through.
// This panel mirrors the rule (`lib/bidResult.ts`) for ONE purpose: so the
// broker sees the explanation box turn required as they type the next-lowest
// figure, instead of after a round-trip. The local hint is never a gate — the
// Save button stays live, and if the two ever disagree the SERVER's refusal is
// what renders.
//
// OUR BID COMES FROM THE ORDER, never from this form. It is the figure the rule
// is measured against, and the broker filed it at submit; asking for it again
// would invite a number that disagrees with the one on the record. The server's
// echoed `ourBidAmount` is preferred when a saved result carries it — that is
// the figure discovery actually used — and the order's own bag is the fallback.
//
// ── THREE REFUSALS, AND TWO LOOK LIKE SUCCESS ────────────────────────────────
//
// `BID_AMOUNT_REQUIRED` (a loss with no next-lowest) and
// `BID_AMOUNT_NOT_APPLICABLE` (a next-lowest on a WIN) are refusals rather than
// quiet corrections, because ignoring either would discard a number the broker
// typed while answering 200. The client's own form prevents both by
// construction — the field only exists on `lost` — so reaching one is a bug
// somewhere, and it still renders inline rather than as a surprise.
//
// ── THE REMINDER'S DEEP LINK LANDS HERE (BRK-S177) ───────────────────────────
//
// PMS's `bid_result_needed` chaser (PMS-S464) mails the broker at 0/1/3/5/10
// days after the bid date, and its template builds
// `/surety/submissions/{id}?focus=bid-result[&result=won|lost]` — three links in
// one message: the panel, "We won", and "We lost". So this panel takes `focus`
// (scroll to it and put the cursor in the outcome control) and `initialOutcome`
// (preselect Won or Lost).
//
// `result` IS A SUGGESTION AND NEVER A RECORDING. The link is an unauthenticated
// GET in somebody's mail client, and a bid result is a write under discovery's
// own >10% rule — so the portal preselects the control and the broker still
// presses Save. PMS says the same thing on its own side of the link, which is
// what keeps the two halves from drifting into "one of us thought the other
// saved it".
//
// A PRESELECTION NEVER TOUCHES A RECORDED RESULT. A saved result stops the
// reminder ladder upstream, so a link carrying `result` should not reach a
// recorded order at all — but a stale one in an old email can, and silently
// proposing a change to an answer the broker already gave is the one behaviour
// this must not have. With a result on file the link scrolls and focuses, and
// the record stays exactly as it reads.

/** The two figures the panel writes, as the server names them — so a refusal's
 *  `details.field` lands under the input it is about. */
const FIELDS = ["nextLowestBidAmount", "explanation"] as const;

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words font-medium text-brk-text">{value}</dd>
    </div>
  );
}

export function BidResultPanel({
  order,
  onUpdated,
  documents,
  onDocumentsChanged,
  focus = false,
  initialOutcome,
}: {
  order: BondSubmission;
  /** The refreshed order — the page owns it, so a saved result and an uploaded
   *  contract both arrive as the server stored them. */
  onUpdated: (order: BondSubmission) => void;
  /**
   * The order's documents, read by the PAGE (BRK-S146). `null` while loading
   * or after a failed read.
   *
   * This panel used to answer "is the signed contract on file?" from
   * `order.finalContract` while the Documents section answered it from the
   * list — TWO sources for one fact, which could disagree, and which rendered
   * the same filename twice on one page. The list is the source now; the
   * pointer on the order view is no longer read here.
   */
  documents: BondDocument[] | null;
  /** Re-read the list — after an upload stores a new contract. */
  onDocumentsChanged: () => void;
  /** Scroll this panel into view and focus its outcome control, on mount.
   *  Set by `?focus=bid-result` (BRK-S177). */
  focus?: boolean;
  /** Preselect Won or Lost without saving. Set by `?result=won|lost`, and
   *  ignored when a result is already on file — see the header. */
  initialOutcome?: "won" | "lost";
}) {
  const { authedRequest, authedUpload } = useAuth();
  // The signed contract as the LIST reports it — the newest, because a
  // re-upload appends a version rather than replacing. Read here rather than
  // off `order.finalContract` so this panel and the Documents section cannot
  // disagree about whether one is on file (BRK-S146).
  const contract = signedContractOf(documents);
  const saved = order.bidResult ?? null;

  // Closed until the broker asks, once a result exists: the record is the thing
  // to read, and a form sitting open over it invites an accidental correction.
  // A deep-linked `?result=` does NOT open it over a recorded result — see the
  // header.
  const [editing, setEditing] = useState(saved === null);
  const [outcome, setOutcome] = useState<"" | "won" | "lost">(
    saved?.outcome ?? (saved === null ? (initialOutcome ?? "") : ""),
  );
  const [nextLowest, setNextLowest] = useState<number | undefined>(
    saved?.nextLowestBidAmount ?? undefined,
  );
  const [explanation, setExplanation] = useState(saved?.explanation ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});

  // The deep link's two targets: the section to scroll to, and the control to
  // leave the cursor in.
  const panel = useRef<HTMLElement>(null);
  const outcomeSelect = useRef<HTMLSelectElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // The echoed figure first — that is the one discovery measured against — then
  // the order's own bag.
  const ourBid =
    saved?.ourBidAmount ??
    ourBidFromOrder(order.values?.[BOND_FINAL_STEP_KEY] ?? null);

  const gap =
    outcome === "lost" &&
    ourBid !== null &&
    nextLowest !== undefined &&
    nextLowest > 0
      ? bidGapPercent(ourBid, nextLowest)
      : null;
  const explanationRequired = bidExplanationRequired({
    outcome: outcome === "" ? "won" : outcome,
    ourBidAmount: ourBid,
    nextLowestBidAmount: nextLowest,
    // Deliberately NOT the typed value: this asks whether the rule APPLIES, so
    // the box can be marked required before they start writing in it. The
    // "have they answered it" half is the disabled check below.
    explanation: null,
  });

  // ONCE, on mount. The page renders this panel only after the order has
  // loaded, so "on mount" is already "once the thing to scroll to exists" — no
  // timer and no second effect keyed on the order.
  //
  // FOCUS, not just scroll: the link's whole promise is that the broker answers
  // without hunting, and a keyboard or screen-reader user who is merely scrolled
  // to a panel is still at the top of the page. `focus()` scrolls on its own in
  // every current engine; `scrollIntoView` runs first anyway so the section's
  // HEADING is what lands in view rather than the control halfway down it.
  useEffect(() => {
    if (!focus) return;
    panel.current?.scrollIntoView({ block: "start" });
    outcomeSelect.current?.focus();
  }, [focus]);

  async function save() {
    if (outcome === "") return;
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      const fresh = await setBondBidResult(authedRequest, order.id, {
        outcome,
        // Sent on `lost` ONLY: a figure on a win is refused upstream rather
        // than ignored, and the form has no reason to invite that refusal.
        ...(outcome === "lost" && nextLowest !== undefined
          ? { nextLowestBidAmount: nextLowest }
          : {}),
        ...(explanation.trim() ? { explanation: explanation.trim() } : {}),
      });
      onUpdated(fresh);
      setEditing(false);
    } catch (err) {
      // The bid result answers `details.field` — SINGULAR — where the rest of
      // this flow sends an array; `anchorServerError` reads both, so the
      // refusal lands under the input it names (BRK-S141).
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    setUploadError(null);
    // The SERVER's own cap and type, restated so the broker learns before the
    // wait rather than after it — a 40 MB scan is a mis-drop, and finding that
    // out post-upload is a minute of their life for nothing.
    if (file.size > FINAL_CONTRACT_MAX_BYTES) {
      setUploadError(
        `That file is too large — please upload a PDF under ${String(FINAL_CONTRACT_MAX_BYTES / (1024 * 1024))} MB.`,
      );
      return;
    }
    setUploading(true);
    try {
      // The upload answers with the stored DOCUMENT's pointer, not the order —
      // the document belongs to the insured's ACCOUNT and the order is only the
      // occasion. So the order is RE-READ to pick up its refreshed
      // `finalContract`, rather than this component assembling one from a
      // response that is not an order.
      await uploadBondFinalContract(authedUpload, order.id, file);
      // BOTH: the order, for anything else its view carries, and the documents
      // LIST, because the contract this just stored has to appear in the
      // section that lists it. Refreshing only the order would leave that
      // section a document behind — which is what having two sources cost.
      onUpdated(await getBondSubmission(authedRequest, order.id));
      onDocumentsChanged();
    } catch (err) {
      setUploadError(
        err instanceof Error
          ? err.message
          : "We couldn’t upload that contract. Please try again.",
      );
    } finally {
      setUploading(false);
      // Cleared so re-picking the SAME file fires `change` again — otherwise a
      // failed upload cannot be retried without choosing a different file.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    <section
      ref={panel}
      // The anchor `?focus=bid-result` names, and the one a "See Documents"-style
      // in-page link would use. Spelled here rather than assembled from the
      // query value, so the URL contract is a constant in the code rather than a
      // string the reader trusts.
      id="section-bid-result"
      aria-labelledby="bid-result"
      className="flex flex-col gap-5 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 id="bid-result" className="text-lg font-semibold text-brk-text">
          Bid result
        </h2>
        {saved !== null && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
          >
            Update
          </button>
        )}
      </div>

      {saved === null && !editing && (
        <p className="text-sm text-brk-dim">
          Let us know how this bid came out — it helps us price the next one.
        </p>
      )}

      {saved !== null && (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Figure
            label="Outcome"
            value={saved.outcome === "won" ? "Won" : "Lost"}
          />
          {saved.nextLowestBidAmount !== null && (
            <Figure
              label="Next lowest bid"
              value={formatBondCurrency(saved.nextLowestBidAmount)}
            />
          )}
          {ourBid !== null && (
            <Figure label="Your bid" value={formatBondCurrency(ourBid)} />
          )}
          {saved.outcome === "lost" &&
            ourBid !== null &&
            saved.nextLowestBidAmount !== null &&
            saved.nextLowestBidAmount > 0 && (
              <Figure
                label="Gap"
                value={`${String(
                  bidGapPercent(ourBid, saved.nextLowestBidAmount),
                )}% below`}
              />
            )}
          <Figure label="Recorded" value={formatBondDate(saved.recordedAt)} />
          {saved.explanation && (
            <div className="sm:col-span-2">
              <Figure label="Explanation" value={saved.explanation} />
            </div>
          )}
        </dl>
      )}

      {editing && (
        <div className="flex flex-col gap-4">
          <SelectField
            ref={outcomeSelect}
            label="How did this bid come out?"
            placeholder="Select…"
            options={[
              { value: "won", label: "Won" },
              { value: "lost", label: "Lost" },
            ]}
            value={outcome}
            onChange={(e) => {
              const next = e.target.value as "" | "won" | "lost";
              setOutcome(next);
              // A next-lowest figure on a WIN is refused upstream rather than
              // ignored, so switching to `won` drops it here rather than
              // carrying a number that cannot be sent.
              if (next !== "lost") setNextLowest(undefined);
            }}
            required
          />

          {outcome === "lost" && (
            <>
              <NumericField
                kind="currency"
                label="Next lowest bid"
                value={nextLowest}
                onChange={setNextLowest}
                required
                error={invalid.nextLowestBidAmount}
              />
              {gap !== null && (
                // LIVE, as they type: the whole reason the rule is mirrored.
                <p className="text-sm text-brk-dim">
                  {gap > 0
                    ? `Your bid is ${String(gap)}% below the next-lowest bid.`
                    : gap < 0
                      ? `Your bid was ${String(Math.abs(gap))}% above the next-lowest bid.`
                      : "Your bid matched the next-lowest bid."}
                </p>
              )}
              <div className="flex w-full flex-col gap-1.5 text-left">
                <label
                  htmlFor="bid-explanation"
                  className="text-sm font-medium text-brk-text"
                >
                  Explanation
                  {explanationRequired && (
                    <span aria-hidden="true" className="ml-1 text-brk-accent">
                      *
                    </span>
                  )}
                </label>
                {explanationRequired && (
                  <p className="text-xs text-brk-dim">
                    More than {String(THRESHOLD_PERCENT)}% below the next-lowest
                    bid — tell us what you knew that the others didn’t.
                  </p>
                )}
                <textarea
                  id="bid-explanation"
                  rows={3}
                  maxLength={2000}
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  aria-required={explanationRequired || undefined}
                  aria-invalid={invalid.explanation ? true : undefined}
                  className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
                />
                {invalid.explanation && (
                  <p role="alert" className="text-sm text-brk-danger">
                    {invalid.explanation}
                  </p>
                )}
              </div>
            </>
          )}

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              // Blocked only on what the FORM knows: an outcome, a figure for a
              // loss, and an explanation the mirrored rule says is required.
              // The server is still the authority on the last one.
              disabled={
                busy ||
                outcome === "" ||
                (outcome === "lost" && nextLowest === undefined) ||
                (explanationRequired && explanation.trim() === "")
              }
              className="rounded-md bg-brk-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save bid result"}
            </button>
            {saved !== null && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                  setInvalid({});
                }}
                className="text-sm text-brk-accent hover:underline"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* The contract, on a WON bid only — there is nothing to sign on a loss. */}
      {saved?.outcome === "won" && (
        <div className="flex flex-col gap-2 border-t border-brk-border pt-5">
          <p className="text-sm font-medium text-brk-text">Signed contract</p>
          {contract ? (
            // ON FILE, and NOT the filename again (BRK-S146). Every document's
            // name, size, date and Download live in ONE place on this page —
            // the Documents section — so the filename is rendered once and
            // cannot be shown two different ways. This panel keeps the
            // occasion, which is its job: a won bid is when a contract exists
            // to upload.
            <p className="flex flex-wrap items-center gap-2 text-sm text-brk-dim">
              <span>
                On file, uploaded {formatBondDate(contract.uploadedAt)}.
              </span>
              <a
                href="#section-documents"
                className="text-brk-accent hover:underline"
              >
                See Documents
              </a>
            </p>
          ) : (
            <p className="text-sm text-brk-dim">
              Upload the signed contract and we’ll issue the bond.
            </p>
          )}
          <input
            ref={fileInput}
            id="final-contract"
            type="file"
            accept={FINAL_CONTRACT_ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <label
            htmlFor="final-contract"
            className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
          >
            {uploading
              ? "Uploading…"
              : contract
                ? "Replace contract"
                : "Upload final contract"}
          </label>
          {uploadError && (
            <p role="alert" className="text-sm text-brk-danger">
              {uploadError}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
